import { Drawer, Flex } from 'antd';
import { DateTime } from 'luxon';
import murmurhash from 'murmurhash';
import React, { useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import CalendarDrawerPanel from '../../calendar/calendarSettingPanel/calendarDrawerPanel';
import { DRAWER_WIDTH, FORMAT_DATE_DISPLAY_DAY_SHORT, GROUP_STATUS_DONE } from '../../const';
import { ComponentsDispatch, RootState } from '../../store';
import { setterGant } from '../../store/slices/gantSlice';
import { ItemTaskType, ItemType, MappingItemIdIndexType, ModeShowingTaskType } from '../../types';
import DrawerPanelListTaskForPeriod from './drawerPanelListTaskForPeriod';
import DrawerPanelListTaskForScheduleItem from './drawerPanelListTaskForScheduleItem';
import SwitchStatusTask from './switchStatusTask';

const DrawerPanelListTask = () => {
    const dispatch = useDispatch<ComponentsDispatch>();

    const [startDate, endDate] = useSelector<RootState, string[]>((state) => state.gant.dayForListTaskShow);
    const scheduleItemForListTaskShow = useSelector<RootState, ItemType | null>(
        (state) => state.gant.scheduleItemForListTaskShow,
    );
    const itemsList = useSelector<RootState, ItemType[]>((state) => state.gantItems.itemsList);
    const mappingItemIdIndex = useSelector<RootState, MappingItemIdIndexType>((state) => state.gant.mappingItemIdIndex);
    const modeShowingTask = useSelector<RootState, ModeShowingTaskType>((state) => state.calendar.modeShowingTask);
    const [open, setOpen] = useState(false);

    const getModeShowingListTask = (): 'listPeriod' | 'listItem' | 'listDay' | 'listChildren' | 'none' => {
        if (scheduleItemForListTaskShow && startDate && startDate !== endDate) {
            return 'listChildren';
        }
        if (scheduleItemForListTaskShow && !startDate) {
            return 'listItem';
        }
        if (!scheduleItemForListTaskShow && startDate) {
            return 'listPeriod';
        }
        if (scheduleItemForListTaskShow && startDate && startDate === endDate) {
            return 'listDay';
        }
        return 'none';
    };

    const modeShowingListTask = getModeShowingListTask();

    const listItemIdForShow = new Set<string>();

    if (
        modeShowingListTask === 'listDay' ||
        modeShowingListTask === 'listChildren' ||
        modeShowingListTask === 'listItem'
    ) {
        const collectChildrenId = (itemId: string) => {
            listItemIdForShow.add(itemId);

            const item = itemsList[mappingItemIdIndex[itemId]];

            if (item?.children?.length) {
                item.children.forEach((childId) => collectChildrenId(childId));
            }
        };

        collectChildrenId(scheduleItemForListTaskShow!.id);
    }

    const listTaskForShow: ItemTaskType[] = [];

    itemsList
        .filter((item) => !scheduleItemForListTaskShow || listItemIdForShow.has(item.id))
        .forEach((item) => {
            item.tasks
                ?.filter((task) => {
                    if (startDate) {
                        const taskDateInMillis = DateTime.fromSQL(task.datetime).toMillis();
                        return (
                            taskDateInMillis >= DateTime.fromSQL(startDate).toMillis() &&
                            taskDateInMillis <= DateTime.fromSQL(endDate).toMillis()
                        );
                    }
                    return true;
                })
                .forEach((task) => {
                    listTaskForShow.push(task);
                });
        });

    const newHashTasksList = murmurhash.v3(JSON.stringify(listTaskForShow));

    const listTaskForShowWithFilter = useMemo(() => {
        return listTaskForShow.filter(
            (task) =>
                (task.status !== GROUP_STATUS_DONE && modeShowingTask === 'Outstanding') ||
                (task.status === GROUP_STATUS_DONE && modeShowingTask === 'Done'),
        );
    }, [newHashTasksList, modeShowingTask]);

    useEffect(() => {
        if (scheduleItemForListTaskShow || startDate || endDate) {
            setOpen(true);
        }
    }, [scheduleItemForListTaskShow, startDate, endDate]);

    const onClose = () => {
        dispatch(setterGant({ scheduleItemForListTaskShow: null, dayForListTaskShow: [] }));
        setOpen(false);
    };

    const getLabelDrawerPanel = () => {
        if (scheduleItemForListTaskShow) {
            return scheduleItemForListTaskShow.name;
        }

        if (startDate) {
            if (startDate === endDate) {
                return DateTime.fromSQL(startDate).toFormat(FORMAT_DATE_DISPLAY_DAY_SHORT);
            }

            return `${DateTime.fromSQL(startDate).toFormat(FORMAT_DATE_DISPLAY_DAY_SHORT)} - ${DateTime.fromSQL(endDate).toFormat(FORMAT_DATE_DISPLAY_DAY_SHORT)}`;
        }
    };

    const countTaskDone = listTaskForShow.filter((task) => task.status === GROUP_STATUS_DONE).length;
    const countTaskOutstanding = listTaskForShow.length - countTaskDone;

    listItemIdForShow.clear();

    const getListItemId = () => {
        const collectParentId = (itemId?: string | null) => {
            if (itemId) {
                listItemIdForShow.add(itemId);
                const item = itemsList[mappingItemIdIndex[itemId]];

                if (item) {
                    collectParentId(item.parentId);
                }
            }
        };

        listTaskForShowWithFilter.forEach((task) => {
            collectParentId(task.schedule_item_id);
        });
    };

    getListItemId();

    return (
        <Drawer
            title={
                <Flex justify={'space-between'}>
                    <div>{`List of task for ${getLabelDrawerPanel()}`}</div>
                    <SwitchStatusTask />
                </Flex>
            }
            onClose={onClose}
            open={open}
            mask={false}
            width={DRAWER_WIDTH}
            // getContainer={false}
            zIndex={1101}
        >
            <Flex vertical style={{ height: '100%' }}>
                <div
                    style={{ flex: 0, padding: '0 0 10px' }}
                >{`${countTaskOutstanding} Outstanding ${countTaskDone} Completed`}</div>
                <div style={{ flex: 1 }}>
                    {(modeShowingListTask === 'listPeriod' || modeShowingListTask === 'listChildren') && (
                        <DrawerPanelListTaskForPeriod
                            listTaskForShow={listTaskForShowWithFilter}
                            listItemIdForShow={listItemIdForShow}
                        />
                    )}
                    {(modeShowingListTask === 'listItem' || modeShowingListTask === 'listDay') && (
                        <DrawerPanelListTaskForScheduleItem listTaskForShow={listTaskForShowWithFilter} />
                    )}
                </div>
            </Flex>
            <CalendarDrawerPanel />
        </Drawer>
    );
};

export default DrawerPanelListTask;
